// @flow
import React from 'react';
import { withRouter } from 'react-router-dom';
import axios from 'axios';
import Modal from 'Components/Modal';
import ModalRedesign from 'Components/ModalRedesign';
import DealerModel from 'Stores/DealerModel';
import styles from './styles.scss';
import { tabs } from 'Utils/getContactTab';
import { gaPush } from 'Utils/ga_push';
import { parse } from 'qs';
import { Container } from 'Components/Grid';
import DeactivatedServiceMessage from '../ContactForm/DeactivatedServiceMessage';
import ContactForm from '../ContactForm';
import { CONTACT_TYPES, MODAL_TYPES } from '../ContactForm/contactFormConstants';
import { withUrlParams } from 'Utils/withUrlParams';
import { getAdjustedModalType } from 'Utils/getModalType';
import { formTypes } from 'Containers/ContactForm/LeadForm/formTypes';

type State = {
  errors: { [string]: ?string },
  contactType: string,
  type: string,
  model: string,
  title: string,
  firstName: string,
  lastName: string,
  street: string,
  houseNumber: string,
  postalCode: string,
  city: string,
  country: string,
  dealer: string,
  telephone: string,
  email: string,
  termsAccepted: boolean,
  showTerms: boolean,
  emailOptIn: boolean,
  telephoneOptIn: boolean,
  smsOptIn: boolean,
  promotionalTermsAccepted: boolean,
  showPromotionalTerms: boolean,
  sent: boolean,
  market: string,
  campaign: string,
  campaignType: string,
  language: string,
  assignToDealer: boolean,
  organisation: string,
  loading: boolean,
};

type Props = {
  dealer: DealerModel,
  type: number,
  model: string,
  isOpen: () => void,
  onClose: () => void,
  history: Object,
  setSharedState: () => void,
  updateMultipleUrlQuery: () => void,
  updateOneUrlQuery: () => void,
  setUrlQuery: () => void,
  urlParams: Object,
  requestedModalIsBlocked: Boolean,
  showMaintenanceModal: Boolean,
};

class ContactModal extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      showOverlayHint: true,
      modalOpened: false,
      contactType: CONTACT_TYPES[props.type || 0],
      contactModel: '',
      closeTracking: {
        event: 'unspecified',
      },
    };

    axios.interceptors.response.use(
      (response) => {
        return response;
      },
      (error) => {
        return Promise.reject(error.response);
      }
    );
  }

  componentDidMount() {
    if (!this.props.requestedModalIsBlocked) {
      this.updateSharedState();
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const { isOpen } = this.props;
    const nextOverflow = isOpen ? 'hidden' : 'unset';

    if (nextOverflow !== document.documentElement.style.getPropertyValue('overflow')) {
      document.documentElement.style.setProperty('overflow', nextOverflow);
    }

    if (!prevProps.isOpen && isOpen) {
      gaPush({
        event: 'gaEvent',
        category: 'modalOpen',
        action: this.getTrackingAction(),
        label: 'true',
      });
    }

    if (Object.keys(prevProps.urlParams).join() !== Object.keys(this.props.urlParams).join()) {
      this.updateSharedState();
    }
  }

  closeOverlayHint = () => {
    const current = this.props.history;
    this.props.history.push({
      pathname: current.location.pathname,
    });
    this.setState({ showOverlayHint: false });
  };

  updateSharedState() {
    const { onClose, setSharedState, type } = this.props;
    const { showmodal, modaltype, carmodel, clienttype } = this.props.urlParams;

    if (showmodal === 'true') {
      onClose(type, '', true);
      const queryParams = {};

      if (clienttype) {
        queryParams.clientType = clienttype;
      }

      if (carmodel) {
        queryParams.carModel = carmodel;
      }

      if (modaltype) {
        queryParams.modalType = modaltype;
      }

      setSharedState(undefined, undefined, queryParams);
    }
  }

  resetModalUrlQuery = () => {
    const queriesToRemove = ['carmodel', 'clientType', 'showmodal', 'modaltype', 'newsletterCode'];
    const queryParams = parse(this.props.location.search.replace('?', ''));
    queriesToRemove.forEach((item) => {
      delete queryParams[item];
      delete queryParams[item.toLowerCase()];
    });
    this.props.setUrlQuery(queryParams);
  };

  getTrackingAction = () => {
    const { urlParams, dealer } = this.props;

    if (!urlParams) return 'modal click';

    if (
      urlParams.modaltype === 'ONLINE_SERVICE_BOOKING' ||
      (urlParams.modaltype === 'SERVICE_APPOINTMENT' && dealer.settings.isOsbUser)
    ) {
      return 'modal osb';
    }

    return 'modal click';
  };

  closeAction = () => {
    const { onClose, history } = this.props;
    const pathname = window.location.pathname.endsWith('/')
      ? window.location.pathname.slice(0, -1)
      : window.location.pathname;

    if (tabs.some((item) => pathname.endsWith(item.href))) {
      history.push('/');
    }

    if (onClose) {
      gaPush({
        event: 'gaEvent',
        category: 'modalOpen',
        action: this.getTrackingAction(),
        label: 'false',
      });
      onClose();
      this.resetModalUrlQuery();
    }
  };

  get modalTypeName() {
    const modalTypeName =
      this.state.validModalTypeFromUrl ||
      Object.values(MODAL_TYPES).find(
        (formType) => formType.contactType === CONTACT_TYPES[this.props.type]
      ).name;

    const { isOsbUser, isSalesPartner, isServicePartner } = this.props.dealer.settings;

    return getAdjustedModalType({
      isServicePartner,
      isSalesPartner,
      isOsbUser,
      initialModalType: modalTypeName,
    });
  }

  render() {
    const { showOverlayHint } = this.state;
    const { isOpen, requestedModalIsBlocked, dealer, showMaintenanceModal, urlParams } = this.props;
    const redesignedModalTypes = formTypes.map((ft) => ft.name);
    const showRedesignedLeadForms = urlParams.modaltype
      ? redesignedModalTypes.includes(urlParams.modaltype)
      : redesignedModalTypes.includes(this.modalTypeName);

    if (requestedModalIsBlocked) {
      return null;
    }

    if (showMaintenanceModal) {
      return (
        <Modal
          isOpen={isOpen}
          onClose={this.closeAction}
          className={styles.contactmodal}
          onOpened={() => {
            this.setState({ modalOpened: true });
          }}
        >
          <Container>
            <DeactivatedServiceMessage
              dealer={dealer}
              showRedesignedLeadForms={showRedesignedLeadForms}
            />
          </Container>
        </Modal>
      );
    }

    if (showRedesignedLeadForms) {
      return (
        <ModalRedesign
          isOpen={isOpen}
          onClose={this.closeAction}
          className={styles.contactmodal}
          onOpened={() => {
            this.setState({ modalOpened: true });
          }}
          closeTracking={this.state.closeTracking}
        >
          <ContactForm
            {...this.props}
            setCloseTracking={(closeTracking) => this.setState({ closeTracking })}
            onAbort={this.closeAction}
            updateOneUrlQuery={this.props.updateOneUrlQuery}
            updateMultipleUrlQuery={this.props.updateMultipleUrlQuery}
          />
        </ModalRedesign>
      );
    }

    return (
      <Modal
        isOpen={isOpen}
        onClose={this.closeAction}
        className={styles.contactmodal}
        onOpened={() => {
          this.setState({ modalOpened: true });
        }}
      >
        <ContactForm
          {...this.props}
          onAbort={this.closeAction}
          updateOneUrlQuery={this.props.updateOneUrlQuery}
          updateMultipleUrlQuery={this.props.updateMultipleUrlQuery}
        />
      </Modal>
    );
  }
}

export default withRouter(withUrlParams(ContactModal));
